import { useEffect, useMemo } from 'react';
import {
  AutocompleteDataSource,
  type AutocompleteOption,
  type DynamicPackageTabProps,
  type HotelTabProps,
  type TabConfiguration,
  TabId,
  type TrainTabProps,
} from '@lmn/common-search-widget';
import type { DynamicPackageFormValues } from '@lmn/common-search-widget/components/tabs/DynamicPackageTab/services/types';
import type { TrainFormValues } from '@lmn/common-search-widget/components/tabs/TrainTab/services/types';
import type { HotelFormValues } from '@lmn/common-search-widget/components/tabs/HotelTab/services/types';
import getCSWPlaces from '@/app/common/CSW/services/adapters/getCSWPlaces';
import {
  getDynamicPackageValues,
  getHotelValues,
  getTrainValues,
} from '@/app/common/CSW/services/adapters/getCSWDefaultValues';
import { LeisureType, SearchModes } from '@/domainLogic/index.ts';
import { isAnywhere, isCountry, isLeisure } from '@/app/components/search/utils/searchTypesUtils.ts';
import { URLParameters } from '@/app/common/urlParameters.ts';
import {
  datesToQuerystring,
  GEO_DESTINATION_PREFIX,
  GEO_DESTINATION_SUB_TYPE,
  getPeople,
} from '@/app/components/search/Utilities';
import getTSXPeopleData from '@/app/common/CSW/services/adapters/getTSXPeopleData';
import type { SearchParams } from '@/server/searchParams';
import getTSXDates from '@/app/common/CSW/services/adapters/getTSXDates';
import { usePageWrapperData } from '@/app/common/pageWrapperProvider.tsx';
import { useCommonClient } from '@/app/common/commonClientProvider.tsx';
import getFlightsUrl from '@/app/components/search/common/getFlightsUrl.ts';
import { useSearchDefaultData } from '@/app/components/search/searchDefaultDataProvider.tsx';
import { toYYYYMMDD } from '@/app/components/search/utils/misc.ts';
import type { DateRangePickerValue } from '@lmn/common-search-widget/components/DateRangePicker/services/types';
import { clientAppLog, createCodeSourceName } from '@/app/components/search/logging/clientAppLog.ts';
import { debounce } from '@/utils/debounce.ts';
import type { UseTabsParams } from './types';
import { isPrompterDestinationOption } from './guards';

export enum PROMPTER_OPTION_TYPE {
  AIRPORT = 'Airport', // indicating an airport with the IATA code, e.g. `BER`.
  ANYWHERE = 'Anywhere', // indicating a destination `Anywhere`.
  CITY = 'City', // indicating a city with Internal City ID, e.g. `154040`.
  COUNTRY = 'Country', // indicating a country with country code, e.g. `PL`.
  HOTEL = 'Hotel', // indicating a hotel with hotel ID.
  REGION = 'Region', // indicating a region with region ID.
  TAG = 'Tag', // indicating a tag, e.g. `IBIZA`.
}

const useTabs = ({ defaultValues, hotelId }: UseTabsParams): TabConfiguration[] | [] => {
  const {
    searchParams,
    localization,
    searchInfoData: { searchMode },
  } = usePageWrapperData();
  const useGeoIdFromPrompter = localization.configValuesApp?.useGeoIdFromPrompter;
  const nearbyAirports = localization?.configValuesApp?.nearbyAirports;
  const { handleNewSearch, setDefaultValuesCSW } = useCommonClient();
  const { defaultDestItems } = useSearchDefaultData();
  const urlFlights = getFlightsUrl(localization);
  const oldDestination = searchParams[URLParameters.DESTINATION];

  const trainPlusHotelEnabled = searchMode === SearchModes.TH;
  const flightPlusHotelEnabled = searchMode === SearchModes.DP;
  const hotelEnabled = searchMode === SearchModes.HO;
  const flightEnabled = searchMode === SearchModes.FF;

  const VALUE_MAP: Record<string, string> = {
    beach: LeisureType.BEACH,
    exotic: LeisureType.EXOTIC,
    ski: LeisureType.SKI,
    citybreaks: LeisureType.CITY_BREAKS,
    spa: LeisureType.SPA,
  };

  // Patch for MPH-621 - don't apply options for White Labels THB and Vibra
  const isPatchWhiteLabel = localization?.whiteLabelName === 'THB' || localization?.whiteLabelName === 'VIBRAHOTELS';

  const getDestination = (place: AutocompleteOption | null, geoDBPlaceValue: string | undefined) => {
    const createAndLogSanitised = (raw: string | undefined, sanitised: string): string => {
      clientAppLog({
        level: 'WARN',
        codeSourceName: createCodeSourceName('useTabs:getDestination'),
        logMessage: `Invalid destination sanitised to "${sanitised}". raw:"${raw}", placeValue:"${place?.value}", cityId:"${(place?.option as { cityId?: string })?.cityId}", optionType:"${(place?.option as { type?: string })?.type}"`,
        url: window.location.href,
      });
      return sanitised;
    };

    // No place, default to Anywhere
    if (!place) return 'A';

    // GeoDB
    if (geoDBPlaceValue) {
      const trimmed = geoDBPlaceValue.trim();
      if (!trimmed) return createAndLogSanitised(geoDBPlaceValue, 'A');
      if (trimmed !== geoDBPlaceValue) return createAndLogSanitised(geoDBPlaceValue, trimmed);
      return trimmed;
    }

    // Non GeoDB
    if (!isPrompterDestinationOption(place.option)) {
      const trimmed = place.value.trim();
      const resolved = VALUE_MAP[trimmed] || trimmed;
      if (!resolved) return createAndLogSanitised(place.value, 'A');
      if (trimmed !== place.value) return createAndLogSanitised(place.value, resolved);
      return resolved;
    }

    if (place?.option?.type === PROMPTER_OPTION_TYPE.TAG) return `TAG_${place.value}`;

    const raw = place.option.cityId || place.value;
    const trimmed = raw?.trim();
    if (!trimmed) return createAndLogSanitised(raw, 'A');
    if (trimmed !== raw) return createAndLogSanitised(raw, trimmed);
    return trimmed;
  };

  const createHighlightHotelId = (place: AutocompleteOption | null, destination: string): SearchParams => {
    if (isLeisure(destination) || isAnywhere(destination) || isCountry(destination)) return {};

    // If user selects a specific hotel from the picker, use it in the MasterData format (without the "HTL-" prefix)
    if (place && isPrompterDestinationOption(place.option) && place.option.type === PROMPTER_OPTION_TYPE.HOTEL) {
      const selectedHotelId = (useGeoIdFromPrompter ? place?.option?.geoId : place?.option?.id) || '';
      return selectedHotelId
        ? {
            [URLParameters.HIGHLIGHT_HOTEL_ID]: selectedHotelId.replace(`${GEO_DESTINATION_SUB_TYPE.HOTEL}-`, ''),
          }
        : {};
    }

    // If no specific hotel place is selected, but we have a hotelId (e.g., date change from hotel detail page)
    // or destination hasn't changed, and we have a hotel to highlight
    if (hotelId && (!place || !isPrompterDestinationOption(place.option) || destination === oldDestination)) {
      return { [URLParameters.HIGHLIGHT_HOTEL_ID]: hotelId.toString() };
    }

    return {};
  };

  const handleNewSearchDebounced = useMemo(() => debounce(handleNewSearch, 300), [handleNewSearch]);
  useEffect(() => handleNewSearchDebounced.cancel, [handleNewSearchDebounced]);

  const formatStayDatesForURL = (stayDates: { mode: string; calendar: { startDate: Date; endDate: Date } }): string => {
    if (!stayDates || stayDates.mode !== 'calendar') return '';
    const { startDate, endDate } = stayDates.calendar;

    return `${toYYYYMMDD(startDate)},${toYYYYMMDD(endDate)}`;
  };

  const hasStayDates = ({
    stayDates,
    dateFrom,
    dateTo,
    isPartialStay = false,
  }: {
    stayDates?: DateRangePickerValue;
    dateFrom: string;
    dateTo: string;
    isPartialStay: boolean;
  }): boolean => {
    if (!isPartialStay || !stayDates || stayDates.mode !== 'calendar') return false;

    const stayStart = toYYYYMMDD(stayDates.calendar.startDate);
    const stayEnd = toYYYYMMDD(stayDates.calendar.endDate);

    return !(stayStart === dateFrom && stayEnd === dateTo);
  };

  const createGeoDBPlaceValue = (placeValue: AutocompleteOption | null) => {
    if (!useGeoIdFromPrompter) return undefined;

    // Hotels are handled differently - the destination is the city and the hotel is specified using the highlightHotelId
    // @ts-ignore
    const cityGeoId = placeValue?.option?.cityGeoId;
    // @ts-ignore
    if (placeValue?.option?.type === PROMPTER_OPTION_TYPE.HOTEL) {
      return cityGeoId || undefined;
    }

    // @ts-ignore
    const geoId = placeValue?.option?.geoId;
    // Countries use the MasterData id, not the geoDB id - the Destinations page is displayed
    // @ts-ignore
    const isCountryType = placeValue?.option?.type === PROMPTER_OPTION_TYPE.COUNTRY;
    return geoId && !isCountryType ? geoId : undefined;
  };

  const onDpSubmit = (values: DynamicPackageFormValues) => {
    const updatedValues = { ...values };
    if (updatedValues.toPlace?.value?.startsWith(GEO_DESTINATION_PREFIX)) {
      updatedValues.toPlace.value = updatedValues.toPlace.value.slice(GEO_DESTINATION_PREFIX.length);
    }
    const geoDBPlaceValue = createGeoDBPlaceValue(values.toPlace);
    if (geoDBPlaceValue && updatedValues.toPlace) {
      updatedValues.toPlace.value = geoDBPlaceValue;
    }
    const destination = getDestination(updatedValues.toPlace, geoDBPlaceValue);

    setDefaultValuesCSW((prevDefaultValuesCSW) => ({
      [TabId.DynamicPackage]: { ...prevDefaultValuesCSW.DP, ...getDynamicPackageValues(updatedValues) },
      [TabId.Flight]: undefined as never,
      [TabId.Hotel]: {},
      [TabId.Train]: {},
    }));

    const { dateFrom, dateTo } = datesToQuerystring(getTSXDates(updatedValues.dates));
    const { adults, ages, hasAges } = getPeople(getTSXPeopleData(updatedValues.participants));
    const { stayDates, isPartialStay } = updatedValues;

    const newSearchParams: SearchParams = {
      [URLParameters.DESTINATION]: destination,
      ...createHighlightHotelId(updatedValues.toPlace, destination),
      [URLParameters.DATE_FROM]: dateFrom,
      [URLParameters.DATE_TO]: dateTo,
      [URLParameters.ADULTS]: adults.toString(),
      [URLParameters.ORIGIN]: updatedValues.fromPlace?.option.code || '',
      [URLParameters.SEARCH_MODE]: SearchModes.DP,
      ...(hasAges ? { [URLParameters.AGES]: ages } : {}),
      ...(updatedValues.class ? { [URLParameters.FLIGHT_CLASS]: updatedValues.class } : {}),
      ...(hasStayDates({ stayDates, dateFrom, dateTo, isPartialStay })
        ? { [URLParameters.CUSTOM_CHECKIN_CHECKOUT]: formatStayDatesForURL(stayDates) }
        : {}),
    };

    handleNewSearchDebounced(newSearchParams);
    window.dispatchEvent(new Event('csw:toggleCollapsedSearch'));
  };

  const onThSubmit = (values: TrainFormValues) => {
    const updatedValues = { ...values };
    if (updatedValues.toPlace?.value?.startsWith(GEO_DESTINATION_PREFIX)) {
      updatedValues.toPlace.value = updatedValues.toPlace.value.slice(GEO_DESTINATION_PREFIX.length);
    }
    const geoDBPlaceValue = createGeoDBPlaceValue(values.toPlace);
    if (geoDBPlaceValue && updatedValues.toPlace) {
      updatedValues.toPlace.value = geoDBPlaceValue;
    }
    const destination = getDestination(updatedValues.toPlace, geoDBPlaceValue);

    setDefaultValuesCSW({
      [TabId.DynamicPackage]: {},
      [TabId.Flight]: undefined as never,
      [TabId.Hotel]: {},
      [TabId.Train]: getTrainValues(updatedValues),
    });

    const { dateFrom, dateTo } = datesToQuerystring(getTSXDates(updatedValues.dates));
    const { adults, ages, hasAges } = getPeople(getTSXPeopleData(updatedValues.participants));

    const newSearchParams: SearchParams = {
      [URLParameters.DESTINATION]: updatedValues.toPlace ? destination : 'A',
      ...createHighlightHotelId(updatedValues.toPlace, destination),
      [URLParameters.DATE_FROM]: dateFrom,
      [URLParameters.DATE_TO]: dateTo,
      [URLParameters.ADULTS]: adults.toString(),
      [URLParameters.ORIGIN]: updatedValues.fromPlace?.option?.value || '',
      [URLParameters.SEARCH_MODE]: SearchModes.TH,
      ...(hasAges ? { [URLParameters.AGES]: ages } : {}),
      ...(updatedValues.class ? { [URLParameters.FLIGHT_CLASS]: updatedValues.class } : {}),
    };

    handleNewSearchDebounced(newSearchParams);
    window.dispatchEvent(new Event('csw:toggleCollapsedSearch'));
  };

  const onHotelSubmit = (values: HotelFormValues) => {
    const updatedValues = { ...values };
    const geoDBPlaceValue = createGeoDBPlaceValue(values.place);
    if (geoDBPlaceValue && updatedValues.place) {
      updatedValues.place.value = geoDBPlaceValue;
    }
    const destination = getDestination(updatedValues.place, geoDBPlaceValue);

    setDefaultValuesCSW({
      [TabId.DynamicPackage]: {},
      [TabId.Flight]: undefined as never,
      [TabId.Hotel]: getHotelValues(updatedValues),
      [TabId.Train]: {},
    });

    const { dateFrom, dateTo } = datesToQuerystring(getTSXDates(updatedValues.dates));
    const { adults, ages, hasAges } = getPeople(getTSXPeopleData(updatedValues.participants));

    const newSearchParams: SearchParams = {
      [URLParameters.DESTINATION]: updatedValues.place ? destination : 'A',
      ...createHighlightHotelId(updatedValues.place, destination),
      [URLParameters.DATE_FROM]: dateFrom,
      [URLParameters.DATE_TO]: dateTo,
      [URLParameters.ADULTS]: adults.toString(),
      [URLParameters.SEARCH_MODE]: SearchModes.HO,
      ...(hasAges ? { [URLParameters.AGES]: ages } : {}),
    };

    handleNewSearchDebounced(newSearchParams);
    window.dispatchEvent(new Event('csw:toggleCollapsedSearch'));
  };

  const dpTabProps: DynamicPackageTabProps = {
    defaultValues: defaultValues?.[TabId.DynamicPackage],
    imagesConfiguration: {
      Hotel: true,
      Country: true,
      Tag: true,
      City: true,
      Region: false,
    },
    dataSources: [
      AutocompleteDataSource.PrompterDestinations,
      ...(isPatchWhiteLabel ? [] : [AutocompleteDataSource.HolidayTypes]),
      AutocompleteDataSource.StaticRecommendations,
      ...(nearbyAirports ? [AutocompleteDataSource.NearbyAirports] : []),
    ],
    ...(isPatchWhiteLabel
      ? {}
      : { staticRecommendations: getCSWPlaces(defaultDestItems?.[0]?.suggestions[0]?.suggestionItems || []) }),
    isFlexibleCalendarEnabled: true,
    isClassEnabled: true,
    onSubmit: onDpSubmit,
    placeTypesConfiguration: {
      Airport: true,
      Anywhere: true,
      City: true,
      Country: true,
      Hotel: true,
      Region: false,
      Tag: true,
    },
    participantsDropdownSettings: {
      maxRooms: 6,
      maxParticipantsPerSearch: 9,
      maxParticipantsPerRoom: 9,
      maxAdultsPerRoom: 9,
    },
    showPastDates: true,
    isPartialStayEnabled: true,
  };

  const thTabProps: TrainTabProps = {
    isTrainEnabled: false,
    defaultValues: defaultValues?.[TabId.Train],
    onSubmit: onThSubmit,
    participantsDropdownSettings: {
      maxRooms: 6,
      maxParticipantsPerSearch: 9,
      maxParticipantsPerRoom: 9,
      maxAdultsPerRoom: 9,
    },
  };

  const hotelTabProps: HotelTabProps = {
    defaultValues: defaultValues?.[TabId.Hotel],
    imagesConfiguration: {
      Hotel: true,
      Country: true,
      Tag: true,
      City: true,
      Region: true,
    },
    dataSources: [
      AutocompleteDataSource.PrompterDestinations,
      AutocompleteDataSource.HolidayTypes,
      AutocompleteDataSource.StaticRecommendations,
    ],
    placeTypesConfiguration: {
      Airport: true,
      Anywhere: true,
      City: true,
      Country: true,
      Hotel: true,
      Region: true,
      Tag: true,
    },
    staticRecommendations: getCSWPlaces(defaultDestItems?.[1]?.suggestions[0]?.suggestionItems || []),
    isFlexibleCalendarEnabled: true,
    onSubmit: onHotelSubmit,
    participantsDropdownSettings: {
      maxRooms: 6,
      maxParticipantsPerSearch: 9,
      maxParticipantsPerRoom: 9,
      maxAdultsPerRoom: 9,
    },
    showPastDates: true,
  };

  const dynamicPackageTab: TabConfiguration = {
    id: TabId.DynamicPackage,
    props: dpTabProps,
    translationKey: 'dp.label',
  };

  const trainTab: TabConfiguration = {
    id: TabId.Train,
    props: thTabProps,
    translationKey: 'train.label',
  };

  const hotelTab: TabConfiguration = {
    id: TabId.Hotel,
    props: hotelTabProps,
    translationKey: 'hotel.label',
  };

  const flightTab: TabConfiguration = {
    id: TabId.Flight,
    redirect: { href: urlFlights, target: 'blank' },
    translationKey: 'flight.label',
  };

  return [
    ...(flightPlusHotelEnabled ? [dynamicPackageTab] : []),
    ...(trainPlusHotelEnabled ? [trainTab] : []),
    ...(hotelEnabled ? [hotelTab] : []),
    ...(flightEnabled ? [flightTab] : []),
  ];
};

export default useTabs;
